export type AgentField = {
  key: string; widget: string; label: string | null; example?: string; options?: string[];
  searchPopup?: string; popupArgs?: Record<string, string | number | boolean | null> | null; pairs?: string;
  concept?: string;
  /**
   * 선택지의 내부값과 보이는 글자 짝. 조회 결과의 같은 이름 열에는 이 내부값이
   * 그대로 들어온다 — `rtclafi` 가 `001`·`002` 다. 짝이 없으면 계산이 `입사` 로
   * 걸러 **0명**을 답한다.
   */
  optionCodes?: { value: string; label: string }[];
};
/**
 * 코드 칸이 고르는 목록. 키는 표 이름이고, 칸이 리터럴 인자를 가지면 `표#인자=값` 이다.
 *
 * `items` 가 없으면 **열거할 수 없는 것**이다 — 거래처 11,270개처럼 크거나
 * (`rows` 만 적힌다), 실행 시점 값이 있어야 열리는 것이다(`unavailable` 에 이유).
 * 그 둘을 가르지 않으면 「목록에 없다」와 「목록을 모른다」가 섞여 맞는 값을 거절한다.
 */
export type CodeList = {
  label: string | null;
  rows?: number;
  codes?: number;
  uses?: number;
  observedAt?: string;
  args?: Record<string, string>;
  openerArgs?: Record<string, string>;
  unavailable?: string;
  items?: { code: string; name: string }[];
};
export type CodeLists = Record<string, CodeList>;

/**
 * 사람이 말한 낱말 → 열의 뜻. `kb/concepts.json` 이 그대로 들어온다.
 *
 * 열에 붙은 `concept` 딱지와 짝이다 — 한쪽은 「이 열은 현장이다」, 이쪽은
 * 「사람이 `현장`·`공사현장` 이라 말하면 그것이다」.
 */
export type Concepts = Record<string, string[]>;

/** 칸이 가리키는 목록을 찾는다. 칸의 리터럴 인자까지 맞춰야 같은 목록이다. */
export function codeListFor(
  field: {
    searchPopup?: string;
    popupArgs?: Record<string, string | number | boolean | null> | null;
  },
  lists: CodeLists | undefined,
): CodeList | undefined {
  if (!lists || !field.searchPopup) return undefined;
  // `null` 은 「인자를 못 읽었다」는 뜻이다 — 그 칸은 다른 칸 값에 따라 목록이 달라진다.
  // 표 이름만으로 조회하면 **남의 목록**으로 검증하게 되고, 맞는 값이 막힌다.
  if (field.popupArgs === null) return undefined;
  const args = field.popupArgs ?? {};
  const suffix = Object.keys(args).length
    ? `#${Object.entries(args).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}=${String(v)}`).join('&')}`
    : '';
  return lists[`${field.searchPopup}${suffix}`];
}

export type AgentColumn = { id: string; text: string; concept?: string; codes?: { value: string; label: string }[] };
export type AgentGrid = {
  key: string;
  columns: AgentColumn[];
  /**
   * 달력으로 그리는 표. **열이 없다** — 그것을 「읽을 것이 없다」로 보면 그 화면이
   * 후보 목록에서 통째로 사라진다(점검계획등록 외 셋, 실측 2026-09-02).
   */
  kind?: 'calendar' | 'observed' | 'record';
  /**
   * **윗 표에서 고른 한 행의 상세.** 목록 전체가 아니다.
   *
   * 비용항목등록의 상세 표를 읽고 「등록된 비용 항목은 총 8건」이라고 답했다 — 47개
   * 그룹 중 첫 그룹의 항목뿐이었다(2026-09-23 실측). 카탈로그가 컨트롤러의 행 포커스
   * 처리기에서 읽어 단다(`catalog/src/focusDetails.ts`).
   */
  detail?: boolean;
  /**
   * **위 표 행마다 이 상세를 펼쳐 읽을 수 있다.** 탐침(`bench/sweep-probe.mjs`)이 행마다 상세가 바뀌는 것을
   * 본 표에만 있다. `rangeKey` 는 위 표에서 범위를 거는 칸(대개 일자)이다.
   */
  master?: {
    stateKey: string; rangeKey?: string; rangeLabel?: string; msPerRow?: number;
    /** 다른 화면과 잇는 법 한 줄(카탈로그 `kb/sweep.json`). 계획 프롬프트에 그대로 간다. */
    joinHint?: string;
  };
  /** 이 표가 탭에만 있으면 그 탭 이름. 펼쳐 읽기 전에 연다 — 행을 고르면 열린 탭 자료만 온다. */
  tab?: string;
  /**
   * **행마다 달린 버튼.** 대개 글자 없는 아이콘이다.
   *
   * ERP 는 문서를 잇는 길을 표 안에 둔다 — 점검결과등록의 `점검일지` 칸에는 A4 모양
   * 아이콘이 행마다 있고 누르면 그 점검의 일지 창이 열린다. 이것을 안 실으면 계획은
   * **그런 길이 있다는 것조차 모른다.**
   *
   * `column` 은 헤더 `id` 이고 DOM 의 `data-el-cell` 과 같다. 위 `columns` 의 이름과
   * 다를 수 있다 — 카탈로그가 칸을 셀이 읽는 필드로 접기 때문이다.
   */
  rowButtons?: { column: string; label: string; method: string }[];
};
export type AgentScreen = {
  route: string;
  name: string;
  module: string;
  /**
   * 사람이 쓴 화면 용도. 형제 화면을 가르는 유일한 단서다 — 이름·칸·표 키만 보면
   * 점검계획등록과 점검결과등록이 거의 같아 계획이 형제를 골랐다(실측 2026-09-07).
   * 설명이 없는 화면도 있으므로 선택이다.
   */
  description?: string;
  fields: AgentField[];
  grids: AgentGrid[];
  actions?: string[];
  /** 표준 액션 밖의 화면 고유 버튼. 문서를 다음 문서로 잇는 길이 여기 있다. */
  buttons?: { label: string; method?: string }[];
  /** 신규 뒤 저장 전에 채울 수 있는 본문 필드. */
  draftFields?: AgentField[];
  /** 화면이 이미 계산해 둔 값. 이름만 프롬프트로 가고 값은 워커로만 간다. */
  scalars?: { key: string; label: string }[];
  /**
   * 탭과 그 탭의 표 상태 키.
   *
   * 정적 추출로는 못 잡는다 — 키가 `state[names[tabIndex]]` 로 실행 중에 계산된다.
   * 화면을 열어 탭을 돌며 그때 나타나는 배열 키를 읽어 담는다.
   */
  tabs?: {
    title: string; stateKey: string; columns?: string[];
    /** 열 키 → 화면에 적힌 이름. 사람 말과 잇는 데 쓴다. */
    labels?: Record<string, string>;
    /** 머리 문서와 한 번에 저장되는 탭 표(카탈로그 `tabSave.ts`). 걸음으로 나누지 않는다. */
    withHead?: boolean;
  }[];
};

type RawColumn = { id: string; text: string; concept?: string; codes?: { value: string; label: string }[] };
type RawGrid = {
  rowsStateKey: string | null;
  columns: RawColumn[];
  /** 행마다 달린 버튼. 카탈로그가 적어 준다. */
  rowButtons?: { column: string; label: string; method: string }[];
};
type RawField = {
  key: string; widget: string; label: string | null; options?: string[];
  optionCodes?: { value: string; label: string }[];
  searchPopup?: string | null; popupArgs?: Record<string, string | number | boolean | null> | null; pairs?: string; concept?: string;
};
type RawScreen = {
  /** 윗 표에서 고른 행의 상세 표들(카탈로그 `focusDetails`). */
  focusDetails?: string[];
  routePath: string | null;
  categoryName: string;
  module: string;
  description?: string;
  searchFields?: RawField[];
  grids?: RawGrid[];
  actions?: string[];
  buttons?: { label: string; method?: string }[];
  draftFields?: RawField[];
};

/**
 * 카탈로그에서 런타임이 쓰는 부분만 남긴다.
 *
 * 전체는 658 KB 이고 이 결과는 103 KB 다. 브라우저로 보내는 것이라 크기가 값이다.
 * 행 키가 없는 표는 결과를 읽을 방법이 없으므로 뺀다 — 있다고 알려 주면 LLM 이
 * 그것을 고르고, 드라이버는 조회 전후 차이로 다른 것을 읽게 된다.
 */
export function buildIndex(catalog: unknown): AgentScreen[] {
  const screens = (catalog as { screens: RawScreen[] }).screens ?? [];
  return screens
    .filter((screen) => screen.routePath)
    .map((screen) => ({
      route: screen.routePath as string,
      name: screen.categoryName,
      module: screen.module,
      // 설명이 없는 화면도 있다. 없으면 필드를 안 만든다 — 지도가 예전과 같아야 한다.
      ...(screen.description ? { description: screen.description } : {}),
      fields: (screen.searchFields ?? []).map((field) => ({
        key: field.key,
        widget: field.widget,
        label: field.label,
        options: field.options,
        optionCodes: field.optionCodes,
        ...(field.searchPopup ? { searchPopup: field.searchPopup } : {}),
        // `null`(인자를 못 읽음)도 통과시킨다. 떨어뜨리면 「인자 없음」과 섞여 남의 목록으로 검증한다.
        ...(field.popupArgs !== undefined ? { popupArgs: field.popupArgs } : {}),
        ...(field.pairs ? { pairs: field.pairs } : {}),
        ...(field.concept ? { concept: field.concept } : {}),
      })),
      grids: (screen.grids ?? [])
        .filter((grid) => grid.rowsStateKey)
        .map((grid) => ({
          key: grid.rowsStateKey as string,
          // 한글 라벨을 버리지 않는다. 코드를 쓰는 모델이 mon 과 misamt 가
          // 무엇인지 아는 유일한 단서다.
          columns: grid.columns.map((column) => ({
            id: column.id,
            text: column.text,
            ...(column.concept ? { concept: column.concept } : {}),
            // 코드 칸의 사전(`01=정상`)도 넘긴다. 없으면 「정상」으로 걸러 0건이 나온다.
            ...(column.codes?.length ? { codes: column.codes } : {}),
          })),
          ...(grid.rowButtons?.length ? { rowButtons: grid.rowButtons } : {}),
          ...(screen.focusDetails?.includes(grid.rowsStateKey as string) ? { detail: true } : {}),
        })),
      actions: screen.actions,
      buttons: screen.buttons,
      draftFields: screen.draftFields?.map((field) => ({
        key: field.key,
        widget: field.widget,
        label: field.label,
        options: field.options,
        optionCodes: field.optionCodes,
        ...(field.searchPopup ? { searchPopup: field.searchPopup } : {}),
        // `null`(인자를 못 읽음)도 통과시킨다. 떨어뜨리면 「인자 없음」과 섞여 남의 목록으로 검증한다.
        ...(field.popupArgs !== undefined ? { popupArgs: field.popupArgs } : {}),
        ...(field.pairs ? { pairs: field.pairs } : {}),
        ...(field.concept ? { concept: field.concept } : {}),
      })),
    }));
}
